import type { MeetupBlock, MeetupSpan } from './meetups';

/**
 * El cuerpo de una ficha de encuentro, de estructura a vnodos (PKG-FORO-ENCUENTROS-POLISH).
 *
 * ESTE FICHERO NO ANALIZA MARKDOWN. La gramatica vive entera en `Umbral\Comunidad\MeetupBody`
 * (PHP) y el resultado llega en el payload como `bodyBlocks`; aqui solo se decide que etiqueta
 * corresponde a cada nodo. Es lo que hace imposible que la pagina del SPA y el HTML server-side
 * de `encuentro.blade.php` digan cosas distintas: no hay dos lectores del mismo texto, hay uno.
 *
 * NO HAY `m.trust()` NI CADENAS DE HTML EN NINGUN PUNTO. Cada trozo de texto del autor se emite
 * como nodo de texto de Mithril, que es la misma garantia que ya daba `paragraphs()` para los
 * comentarios: lo escrito no puede convertirse en marcado porque nunca pasa por un parser de
 * HTML.
 *
 * EL `href` SE VUELVE A COMPROBAR AQUI. El servidor ya lo filtro —`MeetupBody::href`, misma lista
 * de esquemas— y aun asi se repite: este componente recibe JSON de la red, y una defensa que
 * depende de que el otro extremo sea la version que uno cree es una defensa con fecha de
 * caducidad. Son diez lineas y cierran el caso «un payload cacheado de antes de la regla».
 */

/** Espejo de `MeetupBody::SCHEMES`. Todo lo demas se pinta como texto, sin enlace. */
const SCHEMES = ['http:', 'https:', 'mailto:'];

/** Espejo de `MeetupBody::MAX_DEPTH`: mas alla de esto se deja de envolver y se pinta el texto. */
const MAX_DEPTH = 4;

/**
 * `null` cuando la URL no es una que estemos dispuestos a poner en un `href`.
 *
 * El orden importa igual que en PHP: primero se descartan los caracteres que no pintan nada en
 * una URL escrita a mano —espacios, saltos, comillas— porque son justo la forma de colar un
 * esquema partido en dos (`java\nscript:`), y solo despues se mira el esquema.
 */
export function safeHref(raw: string): string | null {
  const url = String(raw || '').trim();

  // Clase escrita con ESCAPES y no con caracteres literales: un byte de control puesto a
  // pelo en el fuente es invisible al leer un diff, y `[ - <]` con espacios literales se lee
  // como un rango de espacio a espacio. Espejo de la clase de `MeetupBody::href`.
  if (url === '' || /[\u0000-\u0020\u007F<>"'`\\]/.test(url)) return null;

  // `//host` hereda el esquema de la pagina, asi que no hay esquema que comprobar.
  if (url.startsWith('//')) return null;

  // Relativa a la raiz: se queda dentro del foro y es la unica forma relativa admitida.
  if (url.startsWith('/')) return url;

  const scheme = /^([a-zA-Z][a-zA-Z0-9+.-]*:)/.exec(url);

  if (!scheme) return null;

  return SCHEMES.indexOf(scheme[1].toLowerCase()) === -1 ? null : url;
}

function spans(nodes: MeetupSpan[] | undefined, depth = 0): any[] {
  if (!Array.isArray(nodes)) return [];

  const out: any[] = [];

  nodes.forEach((node) => {
    if (!node || typeof node !== 'object') return;

    switch (node.type) {
      case 'text':
        out.push(String(node.value ?? ''));
        break;

      case 'break':
        out.push(<br />);
        break;

      case 'strong':
        out.push(depth >= MAX_DEPTH ? spans(node.spans, depth) : <strong>{spans(node.spans, depth + 1)}</strong>);
        break;

      case 'em':
        out.push(depth >= MAX_DEPTH ? spans(node.spans, depth) : <em>{spans(node.spans, depth + 1)}</em>);
        break;

      case 'link': {
        const href = safeHref(node.href);
        const label = spans(node.spans, depth + 1);

        // Sin enlace no se pierde el texto: se pinta la etiqueta suelta, igual que hace el
        // emisor de PHP.
        out.push(href === null ? label : <a href={href}>{label}</a>);
        break;
      }

      // Sin `default`: un tipo que no conocemos se descarta, nunca se pinta crudo.
    }
  });

  return out;
}

/**
 * Los bloques del cuerpo, listos para meter en el `view()` de `EncuentroPage`.
 *
 * Devuelve un array vacio cuando no hay nada que pintar — un cuerpo vacio no deberia existir
 * (`MeetupInput` lo exige) pero un `<p>` vacio en pantalla seria peor que nada.
 */
export default function meetupBody(blocks: MeetupBlock[] | undefined): any[] {
  if (!Array.isArray(blocks)) return [];

  const out: any[] = [];

  blocks.forEach((block) => {
    if (!block || typeof block !== 'object') return;

    switch (block.type) {
      case 'heading':
        // `## ` y `# ` caen los dos en h2 (lo decide el servidor): el titulo de la ficha ya es
        // el h1 de la pagina y un segundo h1 rompe el esquema del documento.
        out.push(Number(block.level) >= 3 ? <h3>{spans(block.spans)}</h3> : <h2>{spans(block.spans)}</h2>);
        break;

      case 'list': {
        const items = (Array.isArray(block.items) ? block.items : []).map((item) => <li>{spans(item)}</li>);

        out.push(block.ordered ? <ol>{items}</ol> : <ul>{items}</ul>);
        break;
      }

      case 'paragraph':
        out.push(<p>{spans(block.spans)}</p>);
        break;

      // Sin `default`: mismo criterio que arriba.
    }
  });

  return out;
}
